import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from './ui/input';
import { MagnifyingGlass, X as XIcon, CheckCircle, Warning } from '@phosphor-icons/react';

/**
 * SupplierSearchSelect
 * ---------------------
 * Strictly select-from-master-list combobox for booking flows.
 *   - Typing filters the dropdown — you CANNOT commit free text.
 *   - Exact-matching the name via paste/keystroke auto-selects the supplier.
 *   - Selected supplier is shown as a read-only chip with a clear (X) button.
 *
 * Props:
 *   suppliers: [{id, name, ...}]
 *   value: string — currently selected supplier NAME
 *   onSelect(supplier|null): fn — supplier object (from list) or null on clear
 *   placeholder, label, required, color ('#00C853'|'#FF3333'), testIdPrefix
 */
export default function SupplierSearchSelect({
  suppliers, value, onSelect, placeholder = 'Search supplier…',
  required = false, color = '#002FA7', testIdPrefix = 'supplier-select',
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);

  const selected = useMemo(
    () => suppliers.find(s => s.name === value) || null,
    [suppliers, value],
  );

  // Close when clicking outside.
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return suppliers.slice(0, 50);
    return suppliers
      .filter(s => s.name?.toLowerCase().includes(q))
      .slice(0, 50);
  }, [query, suppliers]);

  const handlePick = (s) => {
    onSelect(s);
    setQuery('');
    setOpen(false);
    setHighlight(0);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) handlePick(filtered[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Selected state — show as a locked chip with clear button.
  if (selected) {
    return (
      <div
        ref={wrapperRef}
        className="relative w-full border rounded-lg px-3 py-2 flex items-center justify-between gap-2 bg-white"
        style={{ borderColor: `${color}55` }}
        data-testid={`${testIdPrefix}-chip`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <CheckCircle size={16} weight="fill" style={{ color }} />
          <span className="font-medium truncate">{selected.name}</span>
        </div>
        <button
          type="button"
          onClick={handleClear}
          className="text-[#999] hover:text-[#FF3333] p-1 -mr-1 flex-shrink-0"
          aria-label="Change supplier"
          data-testid={`${testIdPrefix}-clear`}
        >
          <XIcon size={14} weight="bold" />
        </button>
      </div>
    );
  }

  // Search state — dropdown combobox (strict list-only).
  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
        <Input
          type="text"
          value={query}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onKeyDown={handleKeyDown}
          className="pl-8 rounded-lg"
          style={{ borderColor: `${color}55` }}
          required={required}
          data-testid={`${testIdPrefix}-input`}
          // NOTE: the form's required attribute guards against pressing submit
          // while nothing is picked — the dropdown never sets the underlying
          // value to a free-text string.
        />
      </div>

      {open && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-[#E5E5E5] rounded-lg shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-[#FF3333]" data-testid={`${testIdPrefix}-no-results`}>
              <Warning size={18} className="mx-auto mb-1" />
              <p className="font-bold uppercase tracking-widest">No match in master list</p>
              <p className="text-[#666] mt-1">Only suppliers in the master list can be booked.<br />Ask admin to add this supplier first.</p>
            </div>
          ) : (
            filtered.map((s, idx) => (
              <button
                key={s.id}
                type="button"
                onClick={() => handlePick(s)}
                onMouseEnter={() => setHighlight(idx)}
                className={`w-full text-left px-3 py-2 text-sm border-b border-[#F3F3F3] last:border-b-0 flex items-center gap-2 ${
                  idx === highlight ? 'bg-[#F3F3F3]' : 'bg-white hover:bg-[#F9F9F9]'
                }`}
                data-testid={`${testIdPrefix}-opt-${s.id}`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: s.lat && s.lng ? '#00C853' : '#FF3333' }}
                  title={s.lat && s.lng ? 'GPS set' : 'No GPS'}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{s.name}</p>
                  {s.address && <p className="text-[10px] text-[#999] truncate">{s.address}</p>}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
